// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Salesperson Mapping page (Stage HRMS-1).
// Map an HRMS salesperson (employee with is_salesperson = true) to each
// customer. Writes only the additive customer salesperson_* columns via
// the mapping API — the existing customer workflow is untouched.
// RBAC server-side (hrms.salesperson_mapping.*).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Input, Select, Space, Spin, Table, Tag, Tooltip, Checkbox, message
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, SolutionOutlined, SearchOutlined, ThunderboltOutlined
} from '@ant-design/icons';

import { hrmsMappingApi } from './hrmsMappingApi';
import { hrEmployeesApi } from '../employees/hrEmployeesApi';
import {
  normalizeMappingRows, isRowDirty, setRowSalesperson, setManySalespersons,
  dirtyCount, buildMappingBulkItems, filterMappingRows
} from './salespersonMappingHelpers';
import '../hrms.css';

export default function SalespersonMappingPage() {
  const [rows, setRows]       = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  const [search, setSearch]   = useState('');
  const [filterSp, setFilterSp] = useState(null);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkSp, setBulkSp]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mapOut, spOut] = await Promise.all([
        hrmsMappingApi.list({ limit: 1000 }),
        hrEmployeesApi.salespersons()
      ]);
      setRows(normalizeMappingRows((mapOut && mapOut.rows) || []));
      setSalespersons(((spOut && spOut.rows) || []).map((s) => ({
        value: s.id,
        label: `${s.displayName || s.employeeName}${s.employeeCode ? ` (${s.employeeCode})` : ''}`
      })));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load mapping.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const spLabel = useMemo(() => {
    const m = new Map();
    salespersons.forEach((s) => m.set(s.value, s.label));
    return m;
  }, [salespersons]);

  const visible = useMemo(
    () => filterMappingRows(rows, { search, salespersonId: filterSp, unassignedOnly }),
    [rows, search, filterSp, unassignedOnly]);

  const nDirty = useMemo(() => dirtyCount(rows), [rows]);

  const setRowSp = (customerId, empId) => setRows((prev) => setRowSalesperson(prev, customerId, empId));

  const applyBulk = () => {
    if (selectedKeys.length === 0) { message.info('Select customers first.'); return; }
    setRows((prev) => setManySalespersons(prev, selectedKeys, bulkSp || null));
    message.success(`${bulkSp ? 'Assigned' : 'Cleared'} salesperson on ${selectedKeys.length} customer(s).`);
    setSelectedKeys([]);
  };

  const saveAll = async () => {
    const items = buildMappingBulkItems(rows);
    if (items.length === 0) { message.info('Nothing to save.'); return; }
    setSaving(true);
    try {
      const out = await hrmsMappingApi.bulkSave(items);
      message.success(`Saved ${out.updated || 0} assigned · ${out.cleared || 0} cleared.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save mappings.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Customer Code', dataIndex: 'customerCode', width: 130, fixed: 'left' },
    { title: 'Customer Name', dataIndex: 'customerName', width: 220 },
    { title: 'GSTIN', dataIndex: 'gstin', width: 160, render: (v) => v || '—' },
    { title: 'City', dataIndex: 'city', width: 130, render: (v) => v || '—' },
    { title: 'State', dataIndex: 'state', width: 150, render: (v) => v || '—' },
    { title: 'Current Salesperson', dataIndex: 'salespersonName', width: 190,
      render: (v) => v ? <Tag color="default">{v}</Tag> : <span style={{ color: '#cbd5e1' }}>Unassigned</span> },
    { title: 'New Salesperson', dataIndex: '_new', width: 240,
      render: (v, row) => (
        <Select size="small" allowClear showSearch optionFilterProp="label"
          style={{ width: '100%' }} placeholder="Assign salesperson…"
          value={v || undefined} options={salespersons}
          status={isRowDirty(row) ? 'warning' : undefined}
          onChange={(val) => setRowSp(row.customerId, val || null)} />
      ) },
    { title: '', key: 'dirty', width: 44, align: 'center',
      render: (_v, row) => isRowDirty(row)
        ? <Tooltip title={`Pending: ${row._new ? (spLabel.get(row._new) || 'assigned') : 'cleared'}`}>
            <span style={{ color: '#d97706', fontWeight: 700 }}>●</span></Tooltip>
        : null }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><SolutionOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Customer Salesperson Mapping</span>
            <span className="hr-headstrip__subtitle">
              Assign a salesperson to each customer. Only active salespersons are listed. Save to persist.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="hr-stat-chip"><strong>{rows.length}</strong> customers</span>
          {nDirty > 0 && (
            <span className="hr-stat-chip hr-stat-chip--dirty"><strong>{nDirty}</strong> unsaved</span>
          )}
        </Space>
      </div>

      <div className="hr-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search customer code, name, GSTIN, city…"
          value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} />
        <Select size="small" allowClear showSearch optionFilterProp="label"
          placeholder="Filter by salesperson" value={filterSp || undefined}
          style={{ width: 200 }} options={salespersons} onChange={(v) => setFilterSp(v || null)} />
        <Checkbox checked={unassignedOnly} onChange={(e) => setUnassignedOnly(e.target.checked)}
          style={{ fontSize: 12.5 }}>Unassigned only</Checkbox>
        <div className="hr-toolbar__spacer" />
        <Select size="small" allowClear showSearch optionFilterProp="label"
          placeholder="Bulk salesperson" value={bulkSp || undefined}
          style={{ width: 200 }} options={salespersons} onChange={(v) => setBulkSp(v || null)} />
        <Tooltip title={selectedKeys.length ? `Apply to ${selectedKeys.length} selected` : 'Select rows first'}>
          <Button size="small" icon={<ThunderboltOutlined />} onClick={applyBulk}
            disabled={selectedKeys.length === 0}>Apply to selected</Button>
        </Tooltip>
        <Tooltip title="Refresh (discards unsaved)"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveAll}
          loading={saving} disabled={saving || nDirty === 0}>Save Mapping</Button>
      </div>

      <div className="hr-card">
        <Spin spinning={loading}>
          <Table className="hr-grid" size="small" rowKey="customerId"
            dataSource={visible} columns={columns}
            rowSelection={{ selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys) }}
            scroll={{ x: 'max-content' }} sticky
            pagination={{ pageSize: 25, showSizeChanger: false, size: 'small' }}
            rowClassName={(r) => isRowDirty(r) ? 'hr-dirty-row' : ''}
            locale={{ emptyText: rows.length === 0
              ? 'No customers found. Add customers in the Customer Master first.'
              : 'No customers match the current filters.' }} />
        </Spin>
      </div>
    </div>
  );
}
